import { Box, TextField, Typography } from "@mui/material"
import { AddOutlined } from "@mui-symbols-material/w300"
import type { FC } from "react"
import { useCallback, useMemo } from "react"
import { useFieldArray, useFormContext, useWatch } from "react-hook-form"

import type { GlPostingAccountOption } from "../hooks/useGlPostingAccountOptions"
import type { GlTransactionFormValues } from "../schemas/glTransactionForm"
import { getExcludedGlPostingAccountIds, getInitialGlPostingAccountOption } from "../utils/glTransactionFormUtils"

import { TransactionLineRow } from "./TransactionLineRow"

import { Button } from "@/components/common/Button"
import { GlTransactionRowType } from "@/graphql/codegen/graphql"

const DEFAULT_LINE = { type: GlTransactionRowType.Debit, glAccountId: null, amount: null }

const formatCurrency = (value: number): string => value.toLocaleString("en-US", { style: "currency", currency: "USD" })

interface TransactionLinesSectionProps {
  disabled?: boolean
  initialSelectedOptions?: Record<number, GlPostingAccountOption>
}

export const TransactionLinesSection: FC<TransactionLinesSectionProps> = ({ disabled, initialSelectedOptions }) => {
  const { control, trigger } = useFormContext<GlTransactionFormValues>()
  const { fields, append, remove } = useFieldArray({ control, name: "transactionLines" })

  const transactionLines = useWatch({ control, name: "transactionLines" })

  const totalDebits = useMemo(
    () =>
      transactionLines
        .filter((line) => line.type === GlTransactionRowType.Debit)
        .reduce((acc, line) => acc + (line.amount ?? 0), 0),
    [transactionLines]
  )

  const totalCredits = useMemo(
    () =>
      transactionLines
        .filter((line) => line.type === GlTransactionRowType.Credit)
        .reduce((acc, line) => acc + (line.amount ?? 0), 0),
    [transactionLines]
  )

  const difference = totalDebits - totalCredits
  const isBalanced = Math.abs(difference) < 0.001
  const debitCount = transactionLines.filter((line) => line.type === GlTransactionRowType.Debit).length
  const creditCount = transactionLines.filter((line) => line.type === GlTransactionRowType.Credit).length

  const handleAddLine = useCallback(async () => {
    append({ ...DEFAULT_LINE })
    await trigger("transactionLines")
  }, [append, trigger])

  const canRemoveLine = (index: number): boolean => {
    const lineType = transactionLines[index]?.type
    switch (lineType) {
      case GlTransactionRowType.Debit:
        return debitCount > 1
      case GlTransactionRowType.Credit:
        return creditCount > 1
      default:
        return fields.length > 2
    }
  }

  return (
    <Box className='flex flex-col gap-3'>
      <Box className='grid grid-cols-[160px_1fr_160px_40px] gap-2 px-1'>
        <Typography variant='body2' className='font-medium text-gray-700'>
          Type
        </Typography>
        <Typography variant='body2' className='font-medium text-gray-700'>
          Account
        </Typography>
        <Typography variant='body2' className='font-medium text-gray-700'>
          Amount
        </Typography>
        <span />
      </Box>

      {fields.map((field, index) => (
        <TransactionLineRow
          key={field.id}
          index={index}
          disabled={disabled}
          initialOption={getInitialGlPostingAccountOption(transactionLines, index, initialSelectedOptions)}
          excludedAccountIds={getExcludedGlPostingAccountIds(transactionLines, index)}
          canRemove={canRemoveLine(index)}
          onRemove={() => remove(index)}
        />
      ))}

      <Box className='flex justify-end'>
        <Box className='flex flex-col items-end gap-1'>
          {!disabled && (
            <Button
              appearance='text'
              startIcon={<AddOutlined />}
              onClick={handleAddLine}
              data-testid='gl-transaction.form-dialog.add-line-button'
              className='w-[260px]'
            >
              Add Another Transaction
            </Button>
          )}

          <TextField
            label='Balance'
            value={formatCurrency(Math.abs(difference))}
            disabled
            size='small'
            data-testid='gl-transaction.form-dialog.difference'
            error={!isBalanced && totalDebits > 0 && totalCredits > 0}
            sx={{
              width: 260,
              "& .MuiInputBase-input.Mui-disabled": {
                color: isBalanced ? "success.dark" : "error.main",
                WebkitTextFillColor: isBalanced ? undefined : "inherit",
              },
            }}
          />
        </Box>
      </Box>

      {!isBalanced && totalDebits > 0 && totalCredits > 0 && (
        <Typography variant='caption' className='text-red-600'>
          Total debits must equal total credits.
        </Typography>
      )}
    </Box>
  )
}
